import { type Readable, derived, writable, type Writable, get } from 'svelte/store';
import {
    ADDRESS_STORE, SESSION, SUBSCRIPTION_STORE, USER_STORE, SESSION_USER, SESSION_ORGANIZATION,
} from '@smatch/cassiopeia-web-components';

import type { User, Address, Organization, Subscription } from 'src/app/types';

const getKey = (): string => `s8:onboarding:${ get(SESSION_USER)?._.key }`;

/* STORE for Skipped Onboarding Steps --------------------------------------------------------- */

interface Skip {
    hasAddedUsers: boolean;
    hasFinancing: boolean;
    hasLogistics: boolean;
}

export const SKIP: Writable<Skip> = writable({
    hasAddedUsers: false,
    hasFinancing: false,
    hasLogistics: false,
});

SESSION_USER.subscribe((user: User | null) => {
    if (user === null) {
        return;
    }

    const DEFAULT_SKIP: Skip = {
        hasAddedUsers: localStorage.getItem(`${ getKey() }:skip:added-users`) === 'true',
        hasFinancing: localStorage.getItem(`${ getKey() }:skip:financing`) === 'true',
        hasLogistics: localStorage.getItem(`${ getKey() }:skip:logistics`) === 'true',
    };

    SKIP.set(DEFAULT_SKIP);

    SKIP.subscribe((skip: Skip) => {
        if (get(SESSION_USER) === null) {
            return;
        }
        localStorage.setItem(`${ getKey() }:skip:users`, String(skip.hasAddedUsers));
        localStorage.setItem(`${ getKey() }:skip:financing`, String(skip.hasFinancing));
        localStorage.setItem(`${ getKey() }:skip:logistics`, String(skip.hasLogistics));
    });
});

/* ONBOARDING STATE STORE ---------------------------------------------------------------------- */

interface OnboardingState {
    hasAddedUsers: boolean;
    hasConfirmedEmail: boolean;
    hasDeliveryAddress: boolean;
    hasFinancing: boolean;
    hasOrganizationData: boolean;
    hasSubscriptions: boolean;
}

const hasAddedUsers = (users: Record<string, Partial<User>> | null): boolean => Object
    .values(users ?? {})
    .filter(user => user._?.key !== get(SESSION_ORGANIZATION)?.managerKey).length > 1;

const hasConfirmedEmail = (user: User): boolean => user._.state === 'confirmed';

const hasDeliveryAddress = (addresses: Record<string, Partial<Address>> | null): boolean => Object
    .values(addresses ?? {})
    .some(address => address.type === 'inbound');

const hasFinancing = ({ legal }: Organization): boolean => {
    if (legal === undefined) {
        return false;
    }

    const { registrationId: REGISTRATION_ID, taxId: TAX_ID, vatId: VAT_ID } = legal;

    return REGISTRATION_ID !== undefined || TAX_ID !== undefined || VAT_ID !== undefined;
};

const hasMainAddress = (addresses: Record<string, Partial<Address>> | null): boolean => Object
    .values(addresses ?? {})
    .some(address => address.type === 'main');

const hasOrganizationType = ({ types }: Organization): boolean => Array.isArray(types) && types.length > 0;

const hasSubscriptions = (subscriptions: Record<string, Partial<Subscription>> | null): boolean => Object
    .values(subscriptions ?? {}).length > 0;


const isOlderThan = (date: string | undefined, days: number): boolean => {
    if (typeof date !== 'string') {
        return false;
    }

    const DATE = new Date(date);
    const NOW = new Date();
    const DELTA = NOW.getTime() - DATE.getTime();

    return DELTA > days * 24 * 60 * 60 * 1000;
};

const isOld = (organization: Organization): boolean => isOlderThan(organization._.createdAt, 14);

const DEFAULT: OnboardingState = {
    hasAddedUsers: false,
    hasConfirmedEmail: false,
    hasDeliveryAddress: false,
    hasFinancing: false,
    hasOrganizationData: false,
    hasSubscriptions: false,
};


export const ONBOARDING_STATE: Readable<OnboardingState> = derived(
    [
        USER_STORE,
        SUBSCRIPTION_STORE,
        ADDRESS_STORE,
        SESSION,
        SKIP,
    ],
    // eslint-disable-next-line max-lines-per-function, complexity
    ([
        $USER_STORE,
        $SUBSCRIPTION_STORE,
        $ADDRESS_STORE,
        $SESSION,
        $SKIP,
    ]) => {
        if ($SESSION === null) {
            return DEFAULT;
        }

        const IS_OLD = isOld($SESSION.organization);

        return {
            hasAddedUsers: hasAddedUsers($USER_STORE) || $SKIP.hasAddedUsers || IS_OLD,
            hasConfirmedEmail: hasConfirmedEmail($SESSION.user),
            hasDeliveryAddress: hasDeliveryAddress($ADDRESS_STORE) || $SKIP.hasLogistics || IS_OLD,
            hasFinancing: hasFinancing($SESSION.organization) || $SKIP.hasFinancing || IS_OLD,
            hasOrganizationData: hasMainAddress($ADDRESS_STORE) && hasOrganizationType($SESSION.organization),
            hasSubscriptions: hasSubscriptions($SUBSCRIPTION_STORE),
        };
    },
);

export const IS_ONBOARDING: Readable<boolean> = derived(
    ONBOARDING_STATE,
    $ONBOARDING_STATE => localStorage.getItem(`${ getKey() }:completed`) !== 'true'
        && Object.values($ONBOARDING_STATE).some(value => value === false),
);


export const ONBOARDING_PROGRESS: Readable<number> = derived(ONBOARDING_STATE, $ONBOARDING_STATE => {
    const TOTAL = Object.keys($ONBOARDING_STATE).length;
    const COMPLETED = Object.values($ONBOARDING_STATE).filter(value => value).length;

    return Math.ceil(COMPLETED / TOTAL * 100);
});

ONBOARDING_PROGRESS.subscribe((progress: number) => {
    if (progress < 100) {
        return;
    }
    localStorage.setItem(`${ getKey() }:completed`, 'true');
});